import React, { useState } from 'react';
import { Ban } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';

import { setBillingCycleStatusVoid } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  VoidClaimsConfirmDialog,
  type VoidCopyOptions,
} from './VoidClaimsButton';

export const RejectedVoidAction: React.FC<{
  id: string;
  status: string;
  onDone: () => void | Promise<void>;
}> = ({ id, status, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { error: showError } = useToast();

  if (status !== 'REJECTED') return null;

  const voidClaim = async (options: VoidCopyOptions) => {
    setConfirming(false);
    setBusy(true);
    try {
      const result = await setBillingCycleStatusVoid({
        path: { billing_cycle_id: id },
        body: {
          copy_worksheet_forward: options.copyWorksheetForward,
          copy_notes_forward: options.copyNotesForward,
        },
      });
      if (result.error) throw result;
    } catch (error) {
      console.error('Failed to void this rejected claim:', error);
      showError(
        getBusinessErrorMessage(error) ?? 'Unable to void this rejected claim.'
      );
      setBusy(false);
      return;
    }

    try {
      await onDone();
    } catch (error) {
      console.error('Failed to refresh after voiding rejected claim:', error);
      showError('Claim voided. Refresh the page to see the updated list.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              disabled={busy}
              onClick={() => setConfirming(true)}
              className='text-muted-foreground hover:text-destructive'
              aria-label='Void rejected claim'
            >
              <Ban className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='bottom' className='text-xs'>
            Void
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <VoidClaimsConfirmDialog
        open={confirming}
        selectedCount={1}
        postSend={true}
        showCopyWorksheetOption={false}
        onConfirm={voidClaim}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
};
